/**
 * Player Store
 *
 * Manages playback state using Zustand.
 * Integrates with Web Audio API and Media Session.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Song } from '@zemer/shared';

type RepeatMode = 'off' | 'all' | 'one';

interface PlayerState {
  // Current playback
  currentSong: Song | null;
  queue: Song[];
  queueIndex: number;

  // Playback state
  isPlaying: boolean;
  progress: number;
  duration: number;
  volume: number;
  muted: boolean;

  // Playback settings
  shuffle: boolean;
  repeat: RepeatMode;

  // UI state
  isExpanded: boolean;

  // Actions
  play: (song: Song, queue?: Song[]) => void;
  toggle: () => void;
  pause: () => void;
  resume: () => void;
  skipNext: () => void;
  skipPrevious: () => void;
  seek: (position: number) => void;
  setVolume: (volume: number) => void;
  toggleMute: () => void;
  toggleShuffle: () => void;
  toggleRepeat: () => void;
  setExpanded: (expanded: boolean) => void;
  setProgress: (progress: number) => void;
  setDuration: (duration: number) => void;
  addToQueue: (songs: Song[]) => void;
  clearQueue: () => void;
}

export const usePlayerStore = create<PlayerState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentSong: null,
      queue: [],
      queueIndex: -1,
      isPlaying: false,
      progress: 0,
      duration: 0,
      volume: 1,
      muted: false,
      shuffle: false,
      repeat: 'off',
      isExpanded: false,

      // Play a song (optionally with a queue)
      play: (song, queue) => {
        const newQueue = queue || [song];
        const index = newQueue.findIndex((s) => s.id === song.id);

        set({
          currentSong: song,
          queue: newQueue,
          queueIndex: index >= 0 ? index : 0,
          isPlaying: true,
          progress: 0,
          duration: 0, // Reset duration for new song
        });

        // Update Media Session
        if ('mediaSession' in navigator) {
          navigator.mediaSession.metadata = new MediaMetadata({
            title: song.title,
            artist: song.artists?.map((a) => a.name).join(', '),
            album: song.album?.name,
            artwork: song.thumbnails?.map((t) => ({
              src: t.url,
              sizes: `${t.width}x${t.height}`,
              type: 'image/jpeg',
            })),
          });
        }
      },

      // Toggle play/pause
      toggle: () => {
        set((state) => ({ isPlaying: !state.isPlaying }));
      },

      pause: () => set({ isPlaying: false }),
      resume: () => set({ isPlaying: true }),

      // Skip to next song
      skipNext: () => {
        const { queue, queueIndex, shuffle, repeat } = get();

        if (queue.length === 0) return;

        let nextIndex: number;

        if (shuffle) {
          // Random next song
          nextIndex = Math.floor(Math.random() * queue.length);
        } else if (queueIndex < queue.length - 1) {
          // Next in queue
          nextIndex = queueIndex + 1;
        } else if (repeat === 'all') {
          // Loop to start
          nextIndex = 0;
        } else {
          // End of queue
          set({ isPlaying: false });
          return;
        }

        const nextSong = queue[nextIndex];
        set({
          currentSong: nextSong,
          queueIndex: nextIndex,
          progress: 0,
          duration: 0,
        });
      },

      // Skip to previous song
      skipPrevious: () => {
        const { queue, queueIndex, progress } = get();

        // If more than 3 seconds in, restart current song
        if (progress > 3) {
          set({ progress: 0 });
          return;
        }

        if (queue.length === 0 || queueIndex <= 0) return;

        const prevSong = queue[queueIndex - 1];
        set({
          currentSong: prevSong,
          queueIndex: queueIndex - 1,
          progress: 0,
        });
      },

      // Seek to position
      seek: (position) => set({ progress: position }),

      // Volume controls
      setVolume: (volume) => set({ volume, muted: false }),
      toggleMute: () => set((state) => ({ muted: !state.muted })),

      // Shuffle/Repeat
      toggleShuffle: () => set((state) => ({ shuffle: !state.shuffle })),
      toggleRepeat: () =>
        set((state) => {
          const modes: RepeatMode[] = ['off', 'all', 'one'];
          const currentIndex = modes.indexOf(state.repeat);
          const nextIndex = (currentIndex + 1) % modes.length;
          return { repeat: modes[nextIndex] };
        }),

      // UI
      setExpanded: (expanded) => set({ isExpanded: expanded }),
      setProgress: (progress) => set({ progress }),
      setDuration: (duration) => set({ duration }),

      // Queue management
      addToQueue: (songs) =>
        set((state) => ({ queue: [...state.queue, ...songs] })),
      clearQueue: () => set({ queue: [], queueIndex: -1 }),
    }),
    {
      name: 'zemer-player',
      partialize: (state) => ({
        volume: state.volume,
        muted: state.muted,
        shuffle: state.shuffle,
        repeat: state.repeat,
      }),
    }
  )
);
